import { z } from 'zod';
import { adminAuditFieldsSchema } from './IAdminAudit';

const rsvpCountsSchema = z.object({
    going: z.number().int().nonnegative(),
    not_going: z.number().int().nonnegative(),
    maybe: z.number().int().nonnegative(),
    guests: z.number().int().nonnegative().default(0),
    attendees: z.number().int().nonnegative().default(0),
});

const entryCountsSchema = z.object({
    rsvp: rsvpCountsSchema,
    comments: z.number().int().nonnegative(),
    media: z.number().int().nonnegative(),
});

export const calendarEntrySchema = z.preprocess((raw) => {
    if (!raw || typeof raw !== 'object') {
        return raw;
    }

    const input = raw as {
        counts?: {
            rsvp?: {
                going?: number;
                GOING?: number;
                not_going?: number;
                notGoing?: number;
                NOT_GOING?: number;
                maybe?: number;
                MAYBE?: number;
                guests?: number;
                attendees?: number;
            };
            comments?: number;
            comment_count?: number;
            media?: number;
            media_count?: number;
        };
        rsvp_counts?: {
            going?: number;
            not_going?: number;
            maybe?: number;
        };
        going_count?: number;
        not_going_count?: number;
        maybe_count?: number;
        comment_count?: number;
        media_count?: number;
    };

    const countsFromInput = input.counts;
    const counts = countsFromInput
        ? {
            rsvp: {
                going: countsFromInput.rsvp?.going ?? countsFromInput.rsvp?.GOING ?? 0,
                not_going: countsFromInput.rsvp?.not_going ?? countsFromInput.rsvp?.notGoing ?? countsFromInput.rsvp?.NOT_GOING ?? 0,
                maybe: countsFromInput.rsvp?.maybe ?? countsFromInput.rsvp?.MAYBE ?? 0,
                guests: countsFromInput.rsvp?.guests ?? 0,
                attendees: countsFromInput.rsvp?.attendees
                    ?? ((countsFromInput.rsvp?.going ?? countsFromInput.rsvp?.GOING ?? 0)
                        + (countsFromInput.rsvp?.guests ?? 0)),
            },
            comments: countsFromInput.comments ?? countsFromInput.comment_count ?? 0,
            media: countsFromInput.media ?? countsFromInput.media_count ?? 0,
        }
        : undefined;

    if (counts) {
        return {
            ...input,
            counts,
        };
    }

    const hasLegacyCounts = typeof input.going_count === 'number'
        || typeof input.not_going_count === 'number'
        || typeof input.maybe_count === 'number'
        || typeof input.comment_count === 'number'
        || typeof input.media_count === 'number'
        || typeof input.rsvp_counts?.going === 'number'
        || typeof input.rsvp_counts?.not_going === 'number'
        || typeof input.rsvp_counts?.maybe === 'number';

    if (!hasLegacyCounts) {
        return input;
    }

    return {
        ...input,
        counts: {
            rsvp: {
                going: input.going_count ?? input.rsvp_counts?.going ?? 0,
                not_going: input.not_going_count ?? input.rsvp_counts?.not_going ?? 0,
                maybe: input.maybe_count ?? input.rsvp_counts?.maybe ?? 0,
                guests: 0,
                attendees: input.going_count ?? input.rsvp_counts?.going ?? 0,
            },
            comments: input.comment_count ?? 0,
            media: input.media_count ?? 0,
        },
    };
}, z.object({
    // Wenn dein Backend eine ID liefert, kannst du sie hier optional einfügen:
    id: z.number().optional(),
    title: z.string().max(200),
    description: z.string().nullable().optional(),
    start_time: z.string(), // ISO-String, z. B. "2025-04-25T10:00:00Z"
    end_time: z.string(),   // ISO-String
    all_day: z.boolean().default(false),
    location: z.string().nullable().optional(),
    calendar_type: z.enum(['event', 'booking']),
    tags: z.array(z.object({
        id: z.number(),
        name: z.string(),
        color: z.string(),
    })).optional(),
    tag_ids: z.array(z.number()).optional(),
    counts: entryCountsSchema.optional(),
    going_count: z.number().int().nonnegative().optional(),
    not_going_count: z.number().int().nonnegative().optional(),
    maybe_count: z.number().int().nonnegative().optional(),
    comment_count: z.number().int().nonnegative().optional(),
    media_count: z.number().int().nonnegative().optional(),
    is_favorite: z.boolean().optional().default(false),
    rsvp_counts: z.object({
        going: z.number().int().nonnegative().optional(),
        not_going: z.number().int().nonnegative().optional(),
        maybe: z.number().int().nonnegative().optional(),
    }).optional(),
}).merge(adminAuditFieldsSchema));

export type ICalendarEntry = z.infer<typeof calendarEntrySchema>;
